import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, BookOpen, ChevronRight, ArrowUp, ArrowDown, Trash2,
  X, Edit2, Check, Route, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Create / Edit Path Modal ─────────────────────────────────────────────────
function PathModal({ onClose, onSave, initial = null }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({ title: title.trim(), description: description.trim() });
      onClose();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="path-modal">
      <div className="bg-[#1a1927] border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-bold text-white">
            {initial ? 'Modifier le parcours' : 'Nouveau parcours'}
          </h2>
          <button onClick={onClose} className="text-[#a0a0ab] hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-[#a0a0ab] mb-1.5 font-medium">Titre *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Ex : Maîtriser le machine learning"
              className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
              data-testid="path-title-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs text-[#a0a0ab] mb-1.5 font-medium">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Décrivez l'objectif de ce parcours…"
              rows={3}
              className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50 resize-none"
              data-testid="path-description-input"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#a0a0ab] text-sm hover:bg-white/5 transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={saving || !title.trim()}
              data-testid="save-path-btn"
              className="flex-1 py-2.5 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold hover:bg-[#ff5555] disabled:opacity-60 transition-colors">
              {saving ? 'Enregistrement…' : (initial ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Path Card (list view) ────────────────────────────────────────────────────
function PathCard({ path, onSelect, onDelete }) {
  const count = path.course_ids?.length || 0;

  return (
    <div
      className="kurso-card p-4 flex items-center gap-4 cursor-pointer hover:-translate-y-0.5 transition-transform duration-200"
      onClick={onSelect}
      data-testid={`path-card-${path.id}`}
    >
      <div className="w-10 h-10 bg-[#ff6b6b]/10 rounded-2xl flex items-center justify-center shrink-0">
        <Route size={18} className="text-[#ff6b6b]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm leading-tight truncate">{path.title}</p>
        {path.description && (
          <p className="text-[#a0a0ab] text-xs mt-0.5 truncate">{path.description}</p>
        )}
        <p className="text-[#e8e8e8] text-xs mt-1">{count} cours</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={e => { e.stopPropagation(); onDelete(path.id); }}
          className="p-1.5 rounded-lg text-[#a0a0ab] hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/10 transition-colors"
          data-testid={`delete-path-${path.id}`}
          title="Supprimer"
        >
          <Trash2 size={13} />
        </button>
        <ChevronRight size={16} className="text-[#a0a0ab]" />
      </div>
    </div>
  );
}

// ── Path Detail View ─────────────────────────────────────────────────────────
function PathDetail({ pathId, onBack, allCourses }) {
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();

  const loadPath = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/paths/${pathId}`, { withCredentials: true });
      setPath(data);
    } catch {
      toast.error('Impossible de charger ce parcours');
      onBack();
    } finally {
      setLoading(false);
    }
  }, [pathId, onBack]);

  useEffect(() => { loadPath(); }, [loadPath]);

  const reorder = async (fromIdx, toIdx) => {
    if (!path) return;
    const newIds = [...path.course_ids];
    const [moved] = newIds.splice(fromIdx, 1);
    newIds.splice(toIdx, 0, moved);
    const updated = { ...path, course_ids: newIds };
    setPath(updated);
    try {
      await axios.put(`${API}/paths/${pathId}`, { course_ids: newIds }, { withCredentials: true });
    } catch {
      toast.error('Erreur lors de la mise à jour');
      loadPath();
    }
  };

  const removeCourse = async (courseId) => {
    if (!path) return;
    const newIds = path.course_ids.filter(id => id !== courseId);
    const updated = { ...path, course_ids: newIds, courses: path.courses.filter(c => c.id !== courseId) };
    setPath(updated);
    try {
      await axios.put(`${API}/paths/${pathId}`, { course_ids: newIds }, { withCredentials: true });
      toast.success('Cours retiré du parcours');
    } catch {
      toast.error('Erreur lors de la mise à jour');
      loadPath();
    }
  };

  const addCourse = async (courseId) => {
    if (!path || path.course_ids.includes(courseId)) return;
    const newIds = [...path.course_ids, courseId];
    try {
      await axios.put(`${API}/paths/${pathId}`, { course_ids: newIds }, { withCredentials: true });
      toast.success('Cours ajouté au parcours');
      loadPath();
      setShowAddCourse(false);
    } catch {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const saveEdit = async (data) => {
    try {
      await axios.put(`${API}/paths/${pathId}`, data, { withCredentials: true });
      toast.success('Parcours mis à jour');
      loadPath();
      setEditMode(false);
    } catch {
      throw new Error('Erreur');
    }
  };

  if (loading) return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => <div key={i} className="kurso-card h-20 animate-shimmer rounded-2xl" />)}
    </div>
  );
  if (!path) return null;

  const availableCourses = allCourses.filter(
    c => !path.course_ids.includes(c.id) && c.status === 'ready'
  );

  return (
    <div className="space-y-5" data-testid="path-detail">
      {/* Header */}
      <div>
        <button onClick={onBack} className="text-[#a0a0ab] text-sm hover:text-white mb-3 flex items-center gap-1 transition-colors">
          ← Mes parcours
        </button>
        <div className="kurso-card p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#ff6b6b]/10 rounded-2xl flex items-center justify-center shrink-0">
              <Route size={22} className="text-[#ff6b6b]" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-xl font-bold text-white leading-tight">{path.title}</h1>
              {path.description && <p className="text-[#a0a0ab] text-sm mt-1">{path.description}</p>}
              <p className="text-[#e8e8e8] text-xs mt-2">{path.courses?.length || 0} cours dans ce parcours</p>
            </div>
            <button
              onClick={() => setEditMode(true)}
              className="p-2 rounded-xl text-[#a0a0ab] hover:text-white hover:bg-white/8 transition-colors"
              data-testid="edit-path-btn"
            >
              <Edit2 size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Course list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-semibold text-white">Cours du parcours</h2>
          <button
            onClick={() => setShowAddCourse(v => !v)}
            className="flex items-center gap-1.5 text-xs text-[#ff6b6b] hover:text-white transition-colors"
            data-testid="add-course-to-path-btn"
          >
            <Plus size={13} /> Ajouter un cours
          </button>
        </div>

        {/* Add course panel */}
        {showAddCourse && availableCourses.length > 0 && (
          <div className="kurso-card p-4 mb-3 border-[#ff6b6b]/20" data-testid="add-course-panel">
            <p className="text-xs text-[#a0a0ab] mb-3">Sélectionner un cours à ajouter :</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {availableCourses.map(c => (
                <button
                  key={c.id}
                  onClick={() => addCourse(c.id)}
                  data-testid={`add-course-${c.id}`}
                  className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/8 text-left transition-colors"
                >
                  <BookOpen size={14} className="text-[#ff6b6b] shrink-0" />
                  <span className="text-sm text-white truncate">{c.title}</span>
                  <Plus size={12} className="text-[#a0a0ab] ml-auto shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
        {showAddCourse && availableCourses.length === 0 && (
          <div className="kurso-card p-4 mb-3 text-center">
            <p className="text-[#a0a0ab] text-sm">Tous vos cours sont déjà dans ce parcours.</p>
          </div>
        )}

        {path.courses?.length === 0 ? (
          <div className="kurso-card p-8 text-center">
            <BookOpen size={28} className="text-[#a0a0ab] mx-auto mb-3" />
            <p className="text-[#a0a0ab] text-sm">Aucun cours dans ce parcours.</p>
            <p className="text-[#e8e8e8] text-xs mt-1">Cliquez sur "Ajouter un cours" pour commencer.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {path.courses.map((course, idx) => (
              <div
                key={course.id}
                className="kurso-card p-3.5 flex items-center gap-3"
                data-testid={`path-course-${course.id}`}
              >
                <div className="w-7 h-7 bg-[#ff6b6b]/10 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold text-[#ff6b6b]">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/course/${course.id}`)}>
                  <p className="text-white text-sm font-medium truncate">{course.title}</p>
                  {course.estimated_hours && (
                    <p className="text-[#a0a0ab] text-xs flex items-center gap-1 mt-0.5">
                      <Clock size={10} /> {course.estimated_hours}h estimées
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => idx > 0 && reorder(idx, idx - 1)}
                    disabled={idx === 0}
                    className="p-1 rounded-lg text-[#a0a0ab] hover:text-white hover:bg-white/8 disabled:opacity-30 transition-colors"
                    data-testid={`move-up-${course.id}`}
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    onClick={() => idx < path.courses.length - 1 && reorder(idx, idx + 1)}
                    disabled={idx === path.courses.length - 1}
                    className="p-1 rounded-lg text-[#a0a0ab] hover:text-white hover:bg-white/8 disabled:opacity-30 transition-colors"
                    data-testid={`move-down-${course.id}`}
                  >
                    <ArrowDown size={13} />
                  </button>
                  <button
                    onClick={() => removeCourse(course.id)}
                    className="p-1 rounded-lg text-[#a0a0ab] hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/10 transition-colors"
                    data-testid={`remove-course-${course.id}`}
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editMode && (
        <PathModal
          initial={path}
          onClose={() => setEditMode(false)}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function Parcours() {
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPathId, setSelectedPathId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadPaths = useCallback(async () => {
    try {
      const [pathsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/paths`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true }),
      ]);
      setPaths(pathsRes.data || []);
      setAllCourses(coursesRes.data || []);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPaths(); }, [loadPaths]);

  const handleCreate = async (data) => {
    const { data: newPath } = await axios.post(`${API}/paths`, data, { withCredentials: true });
    toast.success('Parcours créé !');
    setPaths(prev => [newPath, ...prev]);
    setSelectedPathId(newPath.id);
  };

  const handleDelete = async (pathId) => {
    if (!window.confirm('Supprimer ce parcours ? Les cours ne seront pas supprimés.')) return;
    try {
      await axios.delete(`${API}/paths/${pathId}`, { withCredentials: true });
      toast.success('Parcours supprimé');
      setPaths(prev => prev.filter(p => p.id !== pathId));
      if (selectedPathId === pathId) setSelectedPathId(null);
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-4 space-y-6">
        {/* Header */}
        <div className="animate-fade-up">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">Mes Parcours</h1>
              <p className="text-[#a0a0ab] text-sm mt-1">Organisez vos cours en séquences d'apprentissage</p>
            </div>
            {!selectedPathId && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary flex items-center gap-1.5 px-4 py-2.5 text-sm rounded-xl"
                data-testid="create-path-btn"
              >
                <Plus size={15} /> Nouveau
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="animate-fade-up" style={{ animationDelay: '0.05s' }}>
          {selectedPathId ? (
            <PathDetail
              pathId={selectedPathId}
              onBack={() => { setSelectedPathId(null); loadPaths(); }}
              allCourses={allCourses}
            />
          ) : loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="kurso-card h-20 animate-shimmer rounded-2xl" />)}
            </div>
          ) : paths.length === 0 ? (
            <div className="kurso-card p-10 text-center">
              <Route size={36} className="text-[#a0a0ab] mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-white mb-2">Aucun parcours</h3>
              <p className="text-[#a0a0ab] text-sm mb-5">Créez votre premier parcours pour organiser vos cours en séquence.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary px-6 py-2.5 text-sm rounded-xl"
                data-testid="create-first-path-btn"
              >
                Créer un parcours
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {paths.map(path => (
                <PathCard
                  key={path.id}
                  path={path}
                  onSelect={() => setSelectedPathId(path.id)}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <PathModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
        />
      )}
    </Layout>
  );
}
